import { Overlay } from '@mantine/core';
import { useClickOutside, useTimeout } from '@mantine/hooks';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { recorderClickModeAtom } from '~/atoms/recorder-atoms';

const SHADOW_OFFSET = 2;

const drawPoint = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
  ctx.beginPath();
  ctx.arc(x, y, 5, 0, 2 * Math.PI);
  ctx.fillStyle = color;
  ctx.fill();
};

const drawDashedLine = (
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  lineDashOffset: number,
  color: string,
) => {
  ctx.beginPath();
  ctx.setLineDash([15, 10]);
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(toX, toY);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineDashOffset = lineDashOffset;
  ctx.stroke();
  ctx.setLineDash([]); // Reset to solid lines for other drawings
};

export default function DragToOverlay({
  initPos,
  videoSize,
  zIndex = 200,
  onClickOutsideOver,
  onInsideClick,
  onContextClick,
}: {
  initPos: { x: number; y: number };
  videoSize: { width: number; height: number } | undefined;
  zIndex?: number;
  onClickOutsideOver?: () => void;
  onInsideClick?: (mousePosRatio: { x: number; y: number }) => void;
  onContextClick?: (e: React.MouseEvent, mousePosRatio: { x: number; y: number }) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>(initPos);
  const [isVisible, setIsVisible] = useState(false); // for outline animation
  const lineDashOffset = useRef(1000);

  const [recorderClick, setRecorderClickMode] = useAtom(recorderClickModeAtom);

  // Since many users forget to set the recorder mode to relative when using dragTo,
  // we set it to relative by default when the component mounts and reset it when unmounting.
  useEffect(() => {
    const prevRecorderClick = recorderClick;
    setRecorderClickMode('relative');
    return () => setRecorderClickMode(prevRecorderClick);
  }, []);

  const getMousePos = useCallback((canvas: HTMLCanvasElement, e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvas.getBoundingClientRect(); // will be the same as the video element
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = width;
    canvas.height = height;
  }, []);

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (!canvasRef.current) {
        return;
      }
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      drawDashedLine(
        ctx,
        initPos.x - SHADOW_OFFSET,
        initPos.y + SHADOW_OFFSET,
        mousePos.x - SHADOW_OFFSET,
        mousePos.y + SHADOW_OFFSET,
        lineDashOffset.current,
        '#334155',
      ); // Shadow
      drawDashedLine(ctx, initPos.x, initPos.y, mousePos.x, mousePos.y, lineDashOffset.current, '#E2E8F0'); // Dashed Line

      drawPoint(ctx, initPos.x, initPos.y, '#E2E8F0'); // Initial point
      drawPoint(ctx, mousePos.x, mousePos.y, '#E2E8F0'); // Mouse point
    },
    [initPos, mousePos, lineDashOffset],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    let animationFrameId: number;
    let lastUpdateTime = 0;

    const animate = (currentTime: number) => {
      if (currentTime - lastUpdateTime > 10) {
        // Update every 10ms
        lineDashOffset.current = lineDashOffset.current <= 0 ? 1000 : lineDashOffset.current - 0.5;
        lastUpdateTime = currentTime;
      }
      draw(ctx);
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [handleResize, draw]);

  const clickOutsideRef = useClickOutside(() => {
    onClickOutsideOver?.();
  });

  useTimeout(() => setIsVisible(true), 50, { autoInvoke: true });

  return (
    <div
      ref={clickOutsideRef}
      className="overflow absolute inset-0 flex cursor-crosshair items-center justify-center"
      onClick={() => {
        onInsideClick?.(mousePos);
      }}
      onContextMenu={(e) => {
        onContextClick?.(e, mousePos);
      }}
      style={{
        width: videoSize?.width,
        height: videoSize?.height,
      }}
    >
      <canvas
        ref={canvasRef}
        onMouseMove={(e) => {
          if (!canvasRef.current) {
            return;
          }
          const pos = getMousePos(canvasRef.current, e);
          setMousePos(pos);
        }}
        className={`h-full w-full transition-opacity duration-200 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        style={{ zIndex: zIndex + 1 }}
      />
      <Overlay
        zIndex={zIndex}
        className={`outline outline-offset-[-4px] outline-slate-200 transition-[outline-width] duration-75 ease-in-out ${isVisible ? 'outline-4' : 'outline-0'} `}
      />
    </div>
  );
}
